import { useEffect, useState } from "react";
import {
  Title,
  Text,
  Card,
  Group,
  Avatar,
  Stack,
  TextInput,
  Button,
  NavLink,
  Grid,
  Divider,
  Box,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconUser,
  IconShield,
  IconBell,
  IconPalette,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../lib/services";
import { supabase } from "../lib/supabase";

type Section = "profile" | "account" | "notifications" | "appearance";

const sections: { value: Section; label: string; icon: typeof IconUser; disabled?: boolean }[] = [
  { value: "profile", label: "Profile", icon: IconUser },
  { value: "account", label: "Account", icon: IconShield, disabled: true },
  { value: "notifications", label: "Notifications", icon: IconBell, disabled: true },
  { value: "appearance", label: "Appearance", icon: IconPalette, disabled: true },
];

export function ProfilePage() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<Section>("profile");

  return (
    <>
      <Title order={2} mb="lg">
        Profile Settings
      </Title>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Stack gap={2}>
            {sections.map((s) => (
              <NavLink
                key={s.value}
                label={s.label}
                leftSection={<s.icon size={18} stroke={1.5} />}
                active={activeSection === s.value}
                disabled={s.disabled}
                onClick={() => !s.disabled && setActiveSection(s.value)}
                variant="light"
                style={{ borderRadius: 6 }}
              />
            ))}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 9 }}>
          {activeSection === "profile" && <ProfileSection user={user} />}
        </Grid.Col>
      </Grid>
    </>
  );
}

function ProfileSection({ user }: { user: ReturnType<typeof useAuth>["user"] }) {
  const currentName =
    user?.user_metadata?.display_name || user?.email?.split("@")[0] || "";
  const [displayName, setDisplayName] = useState(currentName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDisplayName(currentName);
  }, [currentName]);

  const initials = (displayName || "U")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const hasChanges = displayName.trim() !== currentName;

  async function handleSave() {
    const trimmed = displayName.trim();
    if (!trimmed) return;

    setSaving(true);
    try {
      await userService.updateMe({ displayName: trimmed });

      // Also update Supabase user metadata so AuthContext stays in sync
      await supabase.auth.updateUser({
        data: { display_name: trimmed },
      });

      notifications.show({
        title: "Profile updated",
        message: "Your display name has been updated.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Update failed",
        message: "Could not update your profile. Please try again.",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap="lg">
      <Card withBorder padding="lg" radius="md">
        <Group mb="md">
          <Avatar size="lg" radius="xl" color="blue">
            {initials}
          </Avatar>
          <Box>
            <Text fw={600}>{displayName || "User"}</Text>
            <Text size="sm" c="dimmed">
              {user?.email}
            </Text>
          </Box>
        </Group>

        <Divider mb="md" />

        <Stack gap="md" maw={400}>
          <TextInput
            label="Display name"
            placeholder="Your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />

          <TextInput
            label="Email"
            value={user?.email || ""}
            disabled
            description="Email cannot be changed here"
          />

          <Group justify="flex-end">
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges || !displayName.trim()}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
